import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  txtButtonContinue,
  txtButtonFinish,
} from '@config/schedule-visit.config';
import { RequiredAnswersProperty } from '@config/messages.config';
import { GetHeaderMain, ServerResponse, TypeDataInProgress } from '@models';
import { SignupResponse, SignupResponseModel, TypesProperty } from '@models';
import { ToastService } from '@modules';
import { getItemSession } from '@utilities';
import { Observable, Subscription } from 'rxjs';
import { ScheduleVisitService } from './schedule-visit.service';
import { map } from 'rxjs/operators';
import { GoogleAnalyticsService } from 'ngx-google-analytics';

@Component({
  selector: 'ggi-journey-schedule-visit',
  templateUrl: './schedule-visit.component.html',
  styleUrls: ['./schedule-visit.component.scss'],
})
export class ScheduleVisitComponent implements OnInit, OnDestroy {
  public getHeader$: Observable<ServerResponse>;
  public notFoundQueryParams = false;
  public withSession = false;

  public readonly stepsIndicators = [1, 2, 3, 4];

  public currentStep = 1;

  public canUserContinue = false;
  public messageErrorPass: boolean;
  public savePreference: boolean;
  public continueCount = 0;

  public params: GetHeaderMain;

  public readonly txtButtonContinue = txtButtonContinue;
  public readonly txtButtonFinish = txtButtonFinish;

  public subscriptions: Subscription[] = [];

  public bookingSummary: SignupResponse;

  public propertyName: string;

  public projectLogo: string;

  public isPropertyFlow: boolean;

  public hasClientAccount = getItemSession('user', 'application') === 'CLIENT';

  private propertyLabel: string;
  private propertyId: string;

  constructor(
    public readonly service: ScheduleVisitService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly toast: ToastService,
    private readonly gaService: GoogleAnalyticsService
  ) {}

  public ngOnInit(): void {
    this.withSession = !!this.route.snapshot.queryParams.withSession;

    if (this.withSession) {
      this.currentStep = 5;

      const { project, typeProject } = this.service.getDataInProgress();
      this.params = {
        project,
        typeProject,
        typeProperty: '',
      };

      this.scheduleVisitWithSession();
    } else {
      this.service.clearDataInProgress();
      this.params = this.getParams();
    }

    this.getHeader(this.params);

    this.route.queryParams.subscribe((res) => {
      if (res.property) {
        this.isPropertyFlow = true;
        this.propertyId = res.property;
        if (this.propertyLabel) {
          this.service.saveDataInProgress(
            { propertyId: res.property, propertyName: this.propertyLabel },
            TypeDataInProgress.selectProperty
          );
        }
        this.currentStep++;
        this.nextStep(true);
      }
    });
  }

  public ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  private getHeader(params: GetHeaderMain): void {
    this.getHeader$ = this.service.getHeader(params).pipe(
      map((res) => {
        if (this.propertyId) {
          const prop = res.response.typesProperty.find(
            (item: TypesProperty) => item.id === this.propertyId
          );
          if (prop) {
            this.service.saveDataInProgress(
              { propertyId: this.propertyId, propertyName: prop.label },
              TypeDataInProgress.selectProperty
            );
          }
        }
        return res;
      })
    );
  }

  private getParams(): GetHeaderMain {
    const { queryParams } = this.route.snapshot;
    const header = queryParams as GetHeaderMain;
    this.service.saveDataInProgress({ ...header }, TypeDataInProgress.project);
    return queryParams as GetHeaderMain;
  }

  public canContinue(event: boolean): void {
    this.canUserContinue = event;

    this.cdr.detectChanges();
  }

  public nextStep(firstStep?: boolean): void {
    if (!this.canUserContinue && !firstStep) {
      this.continueCount++;
      this.toast.error(RequiredAnswersProperty);
      return;
    }
    this.canUserContinue = false;

    this.propertyName = this.service.getDataInProgress().propertyName;
    this.sendDataToGoogleAnalytics(this.currentStep);

    if (this.isPropertyFlow && this.currentStep === 2) {
      this.currentStep += 1;
    } else if (this.hasClientAccount && this.currentStep === 3) {
      this.savePreference = true;
    } else if (this.currentStep === 4) {
      this.scheduleVisit();
    } else {
      this.currentStep++;
    }

    this.cdr.detectChanges();
  }

  public goBack(): void {
    if (
      this.currentStep === 1 ||
      (this.currentStep === 3 && this.isPropertyFlow)
    ) {
      this.router.navigateByUrl('/');
    } else {
      this.currentStep--;
    }
  }

  public formPassword(event: any): void {
    if (event.password === event.confirmPassword) {
      this.messageErrorPass = true;
    } else {
      this.messageErrorPass = false;
    }
  }

  public loginToCheckIn(): void {
    this.router.navigate(['iniciar-sesion'], {
      queryParams: {
        booking: true,
      },
    });
  }

  public scheduleVisitWithSession(): void {
    const request = this.service.getSignUpWithScheduleVisitData();
    if (!request.interests) {
      request.interests = [];
    }
    this.subscriptions.push(
      this.service.checkIn(request).subscribe((res: SignupResponseModel) => {
        const { response } = res;
        this.bookingSummary = response;
        this.currentStep = 5;
      })
    );
  }

  public scheduleVisit(): void {
    if (this.messageErrorPass) {
      const data = this.service.getSignUpWithScheduleVisitData();
      if (data.name) {
        if (!data.interests) {
          data.interests = [];
        }
        this.subscriptions.push(
          this.service.signup(data).subscribe((res: SignupResponseModel) => {
            const { response } = res;
            this.bookingSummary = response;
            this.currentStep++;
            this.gaService.event(
              'Formulario SmartWalk',
              'Paso 4 - Tipo usuario',
              'Usuario registrado'
            );
          })
        );
      } else {
        this.subscriptions.push(
          this.service
            .scheduleAnonymousVisit(data)
            .subscribe((res: SignupResponseModel) => {
              const { response } = res;
              this.bookingSummary = response;
              this.currentStep++;
              this.gaService.event(
                'Formulario SmartWalk',
                'Paso 4 - Tipo usuario',
                'Usuario invitado'
              );
            })
        );
      }
    } else {
      this.toast.error('Las contraseñas no son iguales');
    }
  }

  public setSignupResponse(data: SignupResponse): void {
    this.bookingSummary = data;
    this.currentStep++;
  }

  private sendDataToGoogleAnalytics(currentStep: number): void {
    switch (currentStep) {
      case 1:
        this.gaService.event('Formulario SmartWalk', 'Inicio formulario');
        break;
      case 2:
        this.gaService.event(
          'Formulario SmartWalk',
          'Paso 1 - Pregunta 1 - Tipo de propiedad',
          this.propertyName
        );
        break;
      case 3:
        if (this.service.getDataInProgress().interests?.length > 0) {
          this.service.getDataInProgress().interests.forEach((element) => {
            this.gaService.event(
              'Formulario SmartWalk',
              'Paso 2 - Pregunta 1 - Puntos de interés',
              element.name
            );
          });
        }
        if (this.service.getDataInProgress().interestExtraNotes) {
          this.gaService.event(
            'Formulario SmartWalk',
            'Paso 2 - Pregunta 2 - Comentarios',
            this.service.getDataInProgress().interestExtraNotes
          );
        }
        if (
          this.service.getDataInProgress().question &&
          this.service.getDataInProgress().answer
        ) {
          this.gaService.event(
            'Formulario SmartWalk',
            'Paso 2 - Pregunta 3 - Medio de comunicación',
            this.service.getDataInProgress().answer
          );
        }
        break;
      case 4:
        this.gaService.event(
          'Formulario SmartWalk',
          'Paso 3 - Día solicitado',
          this.service.getDataInProgress().day
        );
        this.gaService.event(
          'Formulario SmartWalk',
          'Paso 3 - Hora solicitada',
          this.service.getDataInProgress().hour
        );
        break;

      default:
        // No data to send
        break;
    }
  }
}
